<template>
    <section class="historical-rates">
        <p class="text-muted mb-3">Последние известные ставки за всю доступную историю до выбранного расчётного месяца. Для месячного оклада выбирается последняя положительная ставка; разные значения одного месяца требуют проверки. Действующие ставки можно изменить во вкладке «Ставки и правила».</p>
        <div class="historical-rates__filters">
            <label>Сотрудник или объект<b-form-input v-model="query" type="search" placeholder="Фамилия, карточка или объект" /></label>
            <b-button variant="outline-secondary" :disabled="loading" @click="load">Обновить</b-button>
        </div>
        <b-alert v-if="error" show variant="danger">{{ error }}</b-alert>
        <div v-if="loading" role="status" class="py-3"><b-spinner small class="mr-2" />Загружаем прежние ставки…</div>
        <template v-else>
            <p class="small text-muted">Найдено: {{ filtered.length }}. Ноль сохранён как ноль, «нет данных» означает отсутствие числовой ставки. Несопоставленные записи показаны отдельно.</p>
            <div v-if="filtered.length" class="table-responsive">
                <table class="table table-sm table-bordered">
                    <thead><tr><th>Сотрудник</th><th v-for="kind in rateKinds" :key="kind">{{ rateTitle(kind) }}</th></tr></thead>
                    <tbody><tr v-for="row in pagedRows" :key="row.id">
                        <td>
                            <strong>{{ row.source_name }}</strong>
                            <EmployeeLink v-if="row.employee_id" :employee-id="row.employee_id" :fallback="row.employee_name" compact class="d-block small" />
                            <small v-else class="d-block text-muted">Нет связи с карточкой</small>
                            <router-link v-if="!row.employee_id && canResolve" :to="{path: '/timesheets/history-people', query: {q: row.source_name}}" class="small">Сопоставить</router-link>
                        </td>
                        <td v-for="kind in rateKinds" :key="kind">
                            <template v-if="row[kind]">
                            <strong>{{ kind === 'monthly' && row[kind].has_month_variants ? 'Требует проверки' : amount(row[kind].value) }}</strong>
                            <small v-if="row[kind].value !== null" class="d-block text-muted">{{ unit(row[kind]) }} · {{ month(row[kind].period_start) }}</small>
                            <a v-if="row[kind].source_url" :href="row[kind].source_url" target="_blank" rel="noopener noreferrer" class="d-block small">Строка {{ row[kind].source_row }} ↗</a>
                            <small v-if="row[kind].has_month_variants" class="d-block text-warning">{{ kind === 'monthly' ? 'Есть разные оклады в последнем месяце. Требуется выбрать ставку; автоматически она не переносится.' : 'В этом месяце есть разные значения; взята последняя строка.' }}</small>
                            <small v-if="kind === 'monthly'" class="d-block text-muted">Оклад за календарный месяц · все объекты</small>
                            <details><summary class="small">Исходные значения</summary>
                                <div class="small">{{ row[kind].source_name }} · {{ row[kind].object_name || 'Без объекта' }}</div>
                                <div class="small">{{ kind === 'monthly' ? 'V' : kind === 'base' ? 'Z' : 'AB' }} · предыдущая: {{ raw(row[kind].previous) }}</div>
                                <div class="small">{{ kind === 'monthly' ? 'W' : kind === 'base' ? 'AA' : 'AC' }} · указанная: {{ raw(row[kind].entered) }}</div>
                                <div v-if="kind === 'monthly' && row[kind].latest_observation" class="small">Последняя запись W: {{ raw(row[kind].latest_observation.value) }} · {{ month(row[kind].latest_observation.period_start) }}</div>
                                <div v-for="(variant, index) in row[kind].variants || []" :key="index" class="small">
                                    {{ amount(variant.value) }} · {{ month(variant.period_start) }}
                                    <a v-if="variant.source_url" :href="variant.source_url" target="_blank" rel="noopener noreferrer">Строка {{ variant.source_row }} ↗</a>
                                </div>
                            </details>
                            </template><span v-else>—</span>
                        </td>
                    </tr></tbody>
                </table>
            </div>
            <p v-else-if="!error" class="text-muted">Ставок по выбранным условиям нет.</p>
            <b-pagination v-if="filtered.length > 30" v-model="page" :total-rows="filtered.length" :per-page="30" aria-label="Страницы исторических ставок" />
        </template>
    </section>
</template>

<script>
import EmployeeLink from '@/components/EmployeeLink.vue';
import api from '@/utils/api';

export default {
    components: {EmployeeLink},
    name: 'PayrollHistoricalRates',
    props: {periodMonth: {type: String, required: true}, canResolve: Boolean},
    data() { return {throughMonth: '', rows: [], query: '', page: 1, loading: false, error: '', version: 0}; },
    computed: {
        filtered() {
            const normalize = value => String(value || '').toLocaleLowerCase('ru-RU').replace(/ё/g, 'е');
            const query = normalize(this.query).trim();
            return this.rows.filter(row => normalize([row.source_name, ...(row.source_names || []), row.employee_name, row.employee_id,
                ...['base', 'object', 'monthly'].map(kind => row[kind] && row[kind].object_name)].join(' ')).includes(query));
        },
        rateKinds() { return ['base', 'object', 'monthly'].filter(kind => this.rows.some(row => row[kind])); },
        pagedRows() { return this.filtered.slice((this.page - 1) * 30, this.page * 30); }
    },
    watch: {
        periodMonth() { this.load(); },
        query() { this.page = 1; }
    },
    mounted() { this.load(); },
    beforeDestroy() { this.version++; },
    methods: {
        rateTitle(kind) { return {base: 'Последняя ставка · база', object: 'Последняя ставка · объекты', monthly: 'Последний положительный оклад'}[kind]; },
        amount(value) { return value === null || value === undefined ? 'нет данных' : Number(value).toLocaleString('ru-RU', {maximumFractionDigits: 6}); },
        raw(value) { return value === null || value === undefined ? 'пусто' : typeof value === 'number' ? this.amount(value) : String(value); },
        month(value) { return value ? new Date(value + 'T12:00:00').toLocaleDateString('ru-RU', {month: 'long', year: 'numeric'}) : ''; },
        unit(row) { return `${row.currency || 'Валюта не указана'}${row.basis === 'month' ? ' / месяц' : row.basis === 'day' ? ' / день' : row.basis === 'hour' ? ' / час' : ' · единица не указана'}`; },
        async load() {
            const version = ++this.version;
            this.loading = true; this.error = ''; this.rows = []; this.throughMonth = ''; this.page = 1;
            try {
                const {data} = await api.get('/api/payroll/historical-rates', {params: {period_start: `${this.periodMonth}-01`}, ignoreModal: true});
                if (version !== this.version) return;
                this.throughMonth = data.through_month ? data.through_month.slice(0, 7) : ''; this.rows = data.rows;
            } catch (error) { if (version === this.version) this.error = error && error.response && error.response.data && error.response.data.msg || 'Не удалось загрузить прежние ставки.'; }
            finally { if (version === this.version) this.loading = false; }
        }
    }
};
</script>

<style scoped>
.historical-rates__filters { display: flex; align-items: flex-end; flex-wrap: wrap; gap: 12px; margin-bottom: 16px; }
.historical-rates__filters label { margin: 0; font-size: .85rem; min-width: 190px; }
.historical-rates td { vertical-align: top; }
.historical-rates th { white-space: nowrap; }
.historical-rates summary { cursor: pointer; margin-top: 4px; }
</style>
